import React, { createContext, useContext, useEffect, useState } from 'react';
import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    signInWithPopup,
    updateProfile
} from 'firebase/auth';
import { auth, googleProvider, db } from '../firebase';
import { doc, setDoc, getDoc } from 'firebase/firestore';

const AuthContext = createContext({});

// Bootstrap admin emails — allows first-time admin login even before
// a Firestore document exists. The document is auto-created on first login.
const BOOTSTRAP_ADMIN_EMAILS = ['rohan@skillhive.com'];

export const useAuth = () => {
    return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);

    // Sign up with email and password (admin registration)
    const signup = async (email, password, name, phone) => {
        try {
            const result = await createUserWithEmailAndPassword(auth, email, password);

            // Update user profile with name
            await updateProfile(result.user, {
                displayName: name
            });

            // Store additional user data in Firestore with admin role
            await setDoc(doc(db, 'users', result.user.uid), {
                name,
                email,
                phone,
                userType: 'admin',
                role: 'admin',
                createdAt: new Date().toISOString()
            });

            return result;
        } catch (error) {
            throw error;
        }
    };

    // Check if a UID belongs to an admin (checks both 'users' and 'admins' collections)
    const checkIsAdmin = async (uid) => {
        // Check users collection first
        const userDoc = await getDoc(doc(db, 'users', uid));
        if (userDoc.exists()) {
            const d = userDoc.data();
            if (d.userType === 'admin' || d.role === 'admin') return true;
        }
        // Also check dedicated admins collection (created by create-admin.js)
        const adminDoc = await getDoc(doc(db, 'admins', uid));
        if (adminDoc.exists()) return true;
        return false;
    };

    // Sign in with email and password
    const login = async (email, password) => {
        const result = await signInWithEmailAndPassword(auth, email, password);

        // Check Firestore for existing admin record
        const ok = await checkIsAdmin(result.user.uid);
        if (ok) {
            setCurrentUser(result.user);
            setIsAdmin(true);
            return result;
        }

        // Bootstrap: if the email is in the allowed list, auto-create the admin
        // document on first login (the authenticated client CAN write to Firestore).
        if (BOOTSTRAP_ADMIN_EMAILS.includes(email.toLowerCase())) {
            try {
                await setDoc(doc(db, 'users', result.user.uid), {
                    uid: result.user.uid,
                    name: result.user.displayName || email.split('@')[0],
                    email,
                    userType: 'admin',
                    role: 'admin',
                    createdAt: new Date().toISOString(),
                });
                await setDoc(doc(db, 'admins', result.user.uid), {
                    uid: result.user.uid,
                    email,
                    role: 'admin',
                    createdAt: new Date().toISOString(),
                });
                console.log('✅ First-time admin setup complete for', email);
            } catch (writeErr) {
                // Rules may still block — allow login anyway since email is trusted
                console.warn('⚠️ Could not write admin Firestore doc:', writeErr.message);
            }
            setCurrentUser(result.user);
            setIsAdmin(true);
            return result;
        }

        await signOut(auth);
        throw new Error('Unauthorized: Admin access required');
    };

    // Sign in with Google (admin only)
    const loginWithGoogle = async () => {
        try {
            const result = await signInWithPopup(auth, googleProvider);
            const ok = await checkIsAdmin(result.user.uid);
            if (!ok) {
                await signOut(auth);
                throw new Error('Unauthorized: Admin access required. Please contact system administrator.');
            }
            setCurrentUser(result.user);
            setIsAdmin(true);
            return result;
        } catch (error) {
            throw error;
        }
    };

    // Logout
    const logout = () => {
        setIsAdmin(false);
        return signOut(auth);
    };

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                try {
                    // Check both 'users' and 'admins' collections
                    const userDoc = await getDoc(doc(db, 'users', user.uid));
                    const adminDoc = await getDoc(doc(db, 'admins', user.uid));

                    const isUserAdmin = userDoc.exists() &&
                        (userDoc.data().userType === 'admin' || userDoc.data().role === 'admin');
                    const isInAdminsCollection = adminDoc.exists();
                    const isBootstrap = BOOTSTRAP_ADMIN_EMAILS.includes(user.email?.toLowerCase());

                    if (isUserAdmin || isInAdminsCollection || isBootstrap) {
                        setCurrentUser(user);
                        setIsAdmin(true);
                    } else {
                        setCurrentUser(null);
                        setIsAdmin(false);
                    }
                } catch (err) {
                    // Firestore permission error — check bootstrap list as fallback
                    console.warn('Auth state check failed:', err.message);
                    if (BOOTSTRAP_ADMIN_EMAILS.includes(user.email?.toLowerCase())) {
                        setCurrentUser(user);
                        setIsAdmin(true);
                    } else {
                        setCurrentUser(null);
                        setIsAdmin(false);
                    }
                }
            } else {
                setCurrentUser(null);
                setIsAdmin(false);
            }
            setLoading(false);
        });

        return unsubscribe;
    }, []);

    const value = {
        currentUser,
        isAdmin,
        loading,
        signup,
        login,
        loginWithGoogle,
        logout
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
